import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { retailerCatalogApi, type ItemsPage } from '../../services/retailerCatalog';
import type { Catalogue, CatalogueMatchSignal, SyncDiff } from '@traaker/shared';
import { DEFAULT_MATCH_SIGNALS } from '@traaker/shared';
import { CatalogueCreateDialog } from './CatalogueCreateDialog';
import { CatalogueEditDialog } from './CatalogueEditDialog';
import { CatalogueIcon } from './CatalogueIcon';
import { MatchConfigCard, SIGNAL_ACCENT_COLOR } from './MatchConfigCard';
import { Flag } from '../dashboard/Flag';
import { languageFlag } from '../dashboard/geoFlags';

/** Tints a column to match the active signal chip's accent color (MatchConfigCard.SIGNAL_ACCENT_COLOR), so toggling a signal visibly maps to the column it reads. */
function highlightCellSx(signal: CatalogueMatchSignal) {
  const accent = SIGNAL_ACCENT_COLOR[signal];
  return accent ? { bgcolor: alpha(accent, 0.1) } : undefined;
}
function highlightHeaderSx(signal: CatalogueMatchSignal) {
  const accent = SIGNAL_ACCENT_COLOR[signal];
  return accent ? { bgcolor: alpha(accent, 0.18), fontWeight: 700, color: accent } : undefined;
}

/**
 * Product module — Catalog page (Phase 1). Two views:
 *  - list of catalogues for the active brand + "Nouveau catalogue"
 *  - detail: metadata, paginated item browse (50/page), refresh-diff review.
 */
export default function ProductCatalog() {
  const { activeBrand } = useBrand();
  const { show } = useToast();
  const brandId = activeBrand?.brandId;

  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<Catalogue | null>(null);

  const reload = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      setCatalogues(await retailerCatalogApi.list(brandId));
    } catch (e: any) {
      show(e?.message || 'Erreur de chargement des catalogues', { severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [brandId, show]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (!brandId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Sélectionnez une marque pour gérer ses catalogues.</Alert>
      </Box>
    );
  }

  if (selected) {
    return (
      <CatalogueDetail
        brandId={brandId}
        catalogue={selected}
        onBack={() => {
          setSelected(null);
          reload();
        }}
        onDeleted={() => {
          setSelected(null);
          reload();
        }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Catalogue</Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          data-tour="product-catalog-new"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Nouveau catalogue
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      ) : catalogues.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'action.hover' }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Aucun catalogue. Créez-en un depuis l'URL du site (détection automatique du sitemap) ou
            en important un fichier CSV.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Créer un catalogue
          </Button>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {catalogues.map((c) => (
            <Paper
              key={c.catalogueId}
              variant="outlined"
              sx={{ p: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
              onClick={() => setSelected(c)}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <CatalogueIcon icon={c.icon} sx={{ color: 'primary.main' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600}>{c.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {c.domain} · {c.language.toUpperCase()}
                  </Typography>
                  {c.description && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.25 }}
                      noWrap
                    >
                      {c.description}
                    </Typography>
                  )}
                </Box>
                <Chip size="small" label={`${c.totalProducts.toLocaleString()} produits`} />
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${c.totalCategories.toLocaleString()} catégories`}
                />
                {c.wafBlocked && <Chip size="small" color="warning" label="WAF" />}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <CatalogueCreateDialog
        open={createOpen}
        brandId={brandId}
        defaultDomain={activeBrand?.url || ''}
        defaultLanguage={activeBrand?.strategy?.targetLanguages?.[0] || 'fr'}
        onClose={() => setCreateOpen(false)}
        onCreated={(cat) => {
          setCreateOpen(false);
          reload();
          setSelected(cat);
        }}
      />
    </Box>
  );
}

// ------------------------------------------------------------------
// Detail view: metadata + paginated item browse + refresh diff.
// ------------------------------------------------------------------
function CatalogueDetail({
  brandId,
  catalogue,
  onBack,
  onDeleted,
}: {
  brandId: string;
  catalogue: Catalogue;
  onBack: () => void;
  onDeleted: () => void;
}) {
  const { show } = useToast();
  const [meta, setMeta] = useState<Catalogue>(catalogue);
  const [signals, setSignals] = useState<CatalogueMatchSignal[]>(
    catalogue.matchSignals?.length ? catalogue.matchSignals : DEFAULT_MATCH_SIGNALS,
  );
  const [tab, setTab] = useState<'product' | 'category'>('product');
  const [page, setPage] = useState<ItemsPage>({ items: [] });
  const [cursors, setCursors] = useState<string[]>([]); // cursor stack for prev
  const [pageIndex, setPageIndex] = useState(0); // 0-based current page
  const [loadingItems, setLoadingItems] = useState(false);
  const [diff, setDiff] = useState<SyncDiff | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const PAGE_SIZE = 50;
  const totalItems = tab === 'product' ? meta.totalProducts : meta.totalCategories;

  const loadItems = useCallback(
    async (cursor?: string) => {
      setLoadingItems(true);
      try {
        const res = await retailerCatalogApi.items(brandId, catalogue.catalogueId, {
          type: tab,
          cursor,
          limit: 50,
        });
        setPage(res);
      } catch (e: any) {
        show(e?.message || 'Erreur de chargement des éléments', { severity: 'error' });
      } finally {
        setLoadingItems(false);
      }
    },
    [brandId, catalogue.catalogueId, tab, show],
  );

  useEffect(() => {
    setCursors([]);
    setPageIndex(0);
    loadItems();
  }, [loadItems]);

  const doPreviewRefresh = async () => {
    setRefreshing(true);
    try {
      const d = await retailerCatalogApi.previewRefresh(brandId, catalogue.catalogueId);
      setDiff(d);
    } catch (e: any) {
      show(e?.message || 'Rafraîchissement impossible', { severity: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const doApplyRefresh = async () => {
    if (!diff) return;
    setRefreshing(true);
    try {
      const updated = await retailerCatalogApi.applyRefresh(brandId, catalogue.catalogueId, diff.date);
      setMeta(updated);
      setDiff(null);
      show('Catalogue mis à jour', { severity: 'success' });
      loadItems();
    } catch (e: any) {
      show(e?.message || 'Application impossible', { severity: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const doDelete = async () => {
    if (!confirm(`Supprimer le catalogue "${meta.name}" ?`)) return;
    try {
      await retailerCatalogApi.remove(brandId, catalogue.catalogueId);
      show('Catalogue supprimé', { severity: 'success' });
      onDeleted();
    } catch (e: any) {
      show(e?.message || 'Suppression impossible', { severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
          Retour
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
          Modifier
        </Button>
        {meta.sourceType !== 'upload' && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={doPreviewRefresh}
            disabled={refreshing}
          >
            Rafraîchir
          </Button>
        )}
        <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={doDelete}>
          Supprimer
        </Button>
      </Stack>

      {refreshing && !diff && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary">
            Récupération du sitemap et calcul des différences…
          </Typography>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CatalogueIcon icon={meta.icon} sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6">{meta.name}</Typography>
        </Stack>
        {meta.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {meta.description}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={meta.domain} />
          <Chip
            size="small"
            icon={<Flag countryCode={languageFlag(meta.language)} size={14} />}
            label={meta.language.toUpperCase()}
          />
          <Chip size="small" label={`Source : ${meta.sourceType}`} />
          <Chip size="small" color="primary" label={`${meta.totalProducts.toLocaleString()} produits`} />
          <Chip size="small" variant="outlined" label={`${meta.totalCategories.toLocaleString()} catégories`} />
          {meta.lastSyncAt && (
            <Chip size="small" variant="outlined" label={`Sync : ${new Date(meta.lastSyncAt).toLocaleDateString()}`} />
          )}
        </Stack>
      </Paper>

      <MatchConfigCard
        brandId={brandId}
        catalogue={meta}
        onChange={(c) => {
          setMeta(c);
          setSignals(c.matchSignals?.length ? c.matchSignals : DEFAULT_MATCH_SIGNALS);
        }}
        onSignalsChange={setSignals}
      />

      {diff && (
        <Alert
          severity={diff.anomalyTripped ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" onClick={() => setDiff(null)}>
                Annuler
              </Button>
              <Button
                color={diff.anomalyTripped ? 'warning' : 'primary'}
                variant="contained"
                size="small"
                onClick={doApplyRefresh}
                disabled={refreshing}
              >
                {diff.anomalyTripped ? 'Confirmer malgré tout' : 'Appliquer'}
              </Button>
            </Stack>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            {diff.addedIds.length} ajout(s) · {diff.removedIds.length} suppression(s)
            {diff.changedIds?.length ? ` · ${diff.changedIds.length} URL modifiée(s)` : ''}
            {' '}(base : {diff.previousCount.toLocaleString()})
          </Typography>
          {diff.anomalyTripped && (
            <Typography variant="body2">
              ⚠️ Changement important détecté — vérifiez qu'il ne s'agit pas d'une erreur de
              récupération du sitemap avant de confirmer.
            </Typography>
          )}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip
          label="Produits"
          color={tab === 'product' ? 'primary' : 'default'}
          onClick={() => setTab('product')}
        />
        <Chip
          label="Catégories"
          color={tab === 'category' ? 'primary' : 'default'}
          onClick={() => setTab('category')}
        />
      </Stack>

      <Paper variant="outlined">
        {loadingItems ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : page.items.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">Aucun élément.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={signals.includes('name') ? highlightHeaderSx('name') : undefined}>Nom</TableCell>
                <TableCell sx={signals.includes('ref') ? highlightHeaderSx('ref') : undefined}>Référence</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell sx={signals.includes('domain') ? highlightHeaderSx('domain') : undefined}>URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {page.items.map((it) => {
                const isHashId = /^[0-9a-f]{16}$/i.test(it.productId);
                const displayRef = it.extractedSku ?? (isHashId ? undefined : it.productId);
                return (
                <TableRow key={it.productId}>
                  <TableCell sx={signals.includes('name') ? highlightCellSx('name') : undefined}>
                    {it.name || <em>—</em>}
                    {it.extractedTitle && it.extractedTitle !== it.name && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {it.extractedTitle}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={signals.includes('ref') ? highlightCellSx('ref') : undefined}>
                    {displayRef ? (
                      <Tooltip title={it.extractedSku ? "extrait de la page produit" : ''}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {displayRef}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <em>—</em>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {it.categoryPath}
                    </Typography>
                  </TableCell>
                  <TableCell sx={signals.includes('domain') ? highlightCellSx('domain') : undefined}>
                    <Link
                      href={it.urlNormalized.startsWith('http') ? it.urlNormalized : `https://${it.urlNormalized}`}
                      target="_blank"
                      rel="noopener"
                      variant="caption"
                    >
                      {it.urlNormalized}
                    </Link>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {page.items.length > 0
            ? `${(pageIndex * PAGE_SIZE + 1).toLocaleString()}–${(
                pageIndex * PAGE_SIZE + page.items.length
              ).toLocaleString()} sur ${totalItems.toLocaleString()} · page ${pageIndex + 1}`
            : `0 sur ${totalItems.toLocaleString()}`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          disabled={pageIndex === 0 || loadingItems}
          onClick={() => {
            setCursors([]);
            setPageIndex(0);
            loadItems();
          }}
        >
          Début
        </Button>
        <Button
          size="small"
          disabled={pageIndex === 0 || loadingItems}
          onClick={() => {
            const prev = [...cursors];
            prev.pop();
            const target = prev[prev.length - 1];
            setCursors(prev);
            setPageIndex((i) => Math.max(0, i - 1));
            loadItems(target);
          }}
        >
          Précédent
        </Button>
        <Button
          size="small"
          disabled={!page.nextCursor || loadingItems}
          onClick={() => {
            setCursors((c) => [...c, page.nextCursor!]);
            setPageIndex((i) => i + 1);
            loadItems(page.nextCursor);
          }}
        >
          Suivant
        </Button>
      </Stack>

      <CatalogueEditDialog
        open={editOpen}
        brandId={brandId}
        catalogue={meta}
        onClose={() => setEditOpen(false)}
        onSaved={(updated) => {
          setMeta(updated);
          setEditOpen(false);
        }}
      />
    </Box>
  );
}
